import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type Application =
  | 'admin'
  | 'agent'
  | 'coordinator'
  | 'photo'
  | 'user'
  | 'designer'
  | 'dcuser';

export enum ProductSuite {
  LISTING_CONCIERGE = 'Listing Concierge',
  DESIGN_CONCIERGE = 'Design Concierge',
}

export class Browser {
  name: string = null;
  version: string = null;
  url: string = null;
  logo: string = null;
  os: string[] = null;
}

/**
 * Private class that is not exposed. Must get values from the AppService.
 * All variables are initialized with default values so that te Object.keys(...)
 * locates all valid variables
 */
class AppConfig {
  app: Application = null;
  productName: ProductSuite = null;
  applicationKey: string = null;
  applicationName: string = null;
  environment: string = null;
  amplitudeGuidesApiKey: string = null;

  persistBaseURL: string = null;
  notifyBaseURL: string = null;
  photosBaseURL: string = null;
  printerBaseURL: string = null;
  batchProcessBaseURL: string = null;
  photographyBaseURL: string = null;
  pdfRenderBaseURL: string = null;
  propertyWebsiteApiBaseURL: string = null;
  propertyWebsiteBaseURL: string = null;

  oktaAuthEnabled: boolean = true;
  oktaClientId: string = null;
  oktaTenantUrl: string = null;
  oktaIssuer: string = null;
  oktaRedirectUri: string = null;

  stripePublishKey: string = null;
  sessionTimeout: number = 5;
  userAgents: Browser[] = [];
  sessionEndWarningInMins: number = 5;
  maxLogoutRetries: number = 3;
  logoutRetryDelay: number = 5000;
  launchDarklyClientID: string = null;
  brandedLoginPageIDP: string = null;
  bhgBrandedLoginPageIDP: string = null;
  eraBrandedLoginPageIDP: string = null;
  c21BrandedLoginPageIDP: string = null;
  cannyBoardToken: string = null;
  smartStreetKey: string = null;
  cannySSOURL: string = null;
  whatsNewURL: string = null;
  productSuiteName: ProductSuite = null;
  serviceNowMcdUrl: string = null;
  serviceNowBrandingKey: string = null;
  serviceNowSkipLoadHistory: number = 1;
  clientReportBaseURL: string = null;
  featureFlags: Array<string | { name: string, value: unknown }> = [];

  /**
   *
   * @param configs Configurations in priority order ([0] = Top Priority)
   */
  constructor(configs: Array<any>) {
    configs = configs || [];
    Object.keys(this).forEach((key) => {
      // Grab the first value in priority order
      const value = configs.map((config) => config[key]).filter((val) => val != null)[0];

      this[key] = value;
    });
  }
}

export class AppService {
  /**
   * App Configuration that is readonly and not exposed. Must be accessed via the
   * AppService static properties to prevent modifications
   */
  private static config: AppConfig;

  // Custom property accessors
  static get app() { return this.get('app'); }
  static get productSuiteName() { return this.get('productSuiteName'); }
  static get isAdminApp(): boolean { return this.app === 'admin'; }
  static get isAgentApp(): boolean { return this.app === 'agent' || this.app === 'user'; }
  static get isLCAgentApp(): boolean { return this.app === 'agent'; }
  static get isLCCoordinatorApp(): boolean { return this.app === 'coordinator'; }
  static get isCoordinatorApp(): boolean { return this.app === 'coordinator' || this.app === 'designer'; }
  static get isPhotoApp(): boolean { return this.app === 'photo'; }
  static get isDCUserApp(): boolean { return this.app === 'user'; }
  static get isDCDesignerApp(): boolean { return this.app === 'designer'; }
  static get isDCApp(): boolean { return this.app === 'user' || this.app === 'dcuser' || this.app === 'designer'; }
  static get isListingConciergeProduct(): boolean { return this.productSuiteName === ProductSuite.LISTING_CONCIERGE; }
  static get isDesignConciergeProduct(): boolean { return this.productSuiteName === ProductSuite.DESIGN_CONCIERGE; }

  static get<Property extends keyof AppConfig>(propertyName: Property): AppConfig[Property] {
    if (this.config == null) {
      throw Error('App has not been initialized. Make sure to run the APP_INITIALIZER');
    }
    return this.config[propertyName];
  }

  static getByString<TValueType = any>(propertyName: string): TValueType {
    if (this.config == null) {
      throw Error('App has not been initialized. Make sure to run the APP_INITIALIZER');
    }
    return this.config[propertyName];
  }

  /**
   * If the appConfig has not been loaded, load the config
   */
  public static async initialize(backend: HttpBackend): Promise<void> {
    if (this.config != null) {
      throw Error('AppService can only be initialized once. Use static properties instead!');
    }

    // If the AppService has not been initialized, load up the configuration values and set the AppConfig
    // properties accordingly
    const http = new HttpClient(backend);

    const [baseConfig, appConfig] = await Promise.all([
      // System-wide configuration
      firstValueFrom(http.get('./assets/configs/config.json')).catch(() => {
        console.log('Error loading base configuration! Using empty configuration');
        return {};
      }),

      // App Wide configuration
      firstValueFrom(http.get('./assets/configs/app-config.json')).catch(() => {
        console.log('Error loading app configuration! Using empty configuration');
        return {};
      }),
    ]);

    this.initializeAppConfig(baseConfig, appConfig);
  }

  /**
   * Initializing the AppConfig using the prioritized configurations in this order:
   * 1.) Custom App Config
   * 2.) App Config
   * 3.) Custom Base Config
   * 4.) Base Config
   */
  private static initializeAppConfig(baseConfig: any, appConfig: any) {
    const customBaseConfig = this.loadEnvironmentConfig(baseConfig);
    const customAppConfig = this.loadEnvironmentConfig(appConfig);

    const priorityConfigs = [customAppConfig, appConfig, customBaseConfig, baseConfig];
    this.config = new AppConfig(priorityConfigs);
  }

  /**
   * Loads developer specific configuration
   */
  private static loadEnvironmentConfig(jsonObject: any) {
    // Read the custom configuration variable
    const { configuration } = jsonObject;
    if (configuration && jsonObject[configuration] != null) {
      return jsonObject[configuration];
    }
    return {};
  }
}
